import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Text,
  useColorModeValue,
  VStack,
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  Alert,
  AlertIcon,
  Spinner,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  useDisclosure,
  Collapse,
  IconButton,
  Checkbox,
  CheckboxGroup,
  Stack,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPlay, FiChevronDown, FiChevronUp, FiRefreshCw, FiMinus, FiPlus } from 'react-icons/fi';
import { secureProjectApi as projectApi } from '../services/csrfApi';
import { v4 } from 'uuid';
import FileUploader from '../components/FileUploader';
import ImagePreviewWithZoom from '../components/ImagePreviewWithZoom';
import PDFPreviewIframe from '../components/PDFPreviewIframe';
import { referenceDataApi } from '../services/referenceDataApi';
import { originalPromptsApi } from '../services/originalPromptsApi';

const OCRProcessing = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  // アップロードした画像情報
  const [uploadedFile, setUploadedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isUploadSectionCollapsed, setIsUploadSectionCollapsed] = useState(false);
  
  // 設定保存用
  const [configName, setConfigName] = useState('');
  
  // OCR設定（ブロックシステム）
  const [ocrConfig, setOcrConfig] = useState({
    preset: "custom",
    customPrompt: "",
    blocks: [
      {
        id: v4(),
        blockName: "基本情報",
        blockType: "header", // "header" or "table"
        items: [
          { id: v4(), itemName: "", columnName: "" },
          { id: v4(), itemName: "", columnName: "" },
        ]
      },
      {
        id: v4(),
        blockName: "明細データ",
        blockType: "table", // "header" or "table"
        items: [
          { id: v4(), itemName: "", columnName: "" },
          { id: v4(), itemName: "", columnName: "" },
        ]
      }
    ],
  });

  // 元の設定を記録（設定選択時の状態を保存）
  const [originalConfig, setOriginalConfig] = useState(null);
  
  // AIプロバイダー選択
  const [aiProvider, setAiProvider] = useState('openai');
  
  // 参照データ関連
  const [useReferenceData, setUseReferenceData] = useState(false);
  const [selectedReferenceData, setSelectedReferenceData] = useState([]);
  const [referenceDataList, setReferenceDataList] = useState([]);

  // プロンプト選択関連
  const [promptMode, setPromptMode] = useState('basic'); // 'basic' または 'original'
  const [selectedOriginalPrompt, setSelectedOriginalPrompt] = useState('');
  const [originalPromptsList, setOriginalPromptsList] = useState([]);

  // 保存された設定関連
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [selectedSavedConfig, setSelectedSavedConfig] = useState('');

  // 参照データ一覧の取得
  const fetchReferenceDataList = async () => {
    try {
      const response = await referenceDataApi.getReferenceDataList(projectId);
      setReferenceDataList(response.data);
    } catch (error) {
      console.error('Failed to fetch reference data list:', error);
      // エラーは静かに処理（参照データは必須ではないため）
    }
  };

  // オリジナルプロンプト一覧の取得
  const fetchOriginalPromptsList = async () => {
    try {
      const response = await originalPromptsApi.getOriginalPrompts(projectId);
      setOriginalPromptsList(response);
    } catch (error) {
      console.error('Failed to fetch original prompts list:', error);
      // エラーは静かに処理（オリジナルプロンプトは必須ではないため）
    }
  };

  // 保存された設定一覧の取得
  const fetchSavedConfigs = async () => {
    try {
      console.log(`プロジェクト固有のOCR設定を取得中: /projects/${projectId}/ocr-configs`);
      const response = await projectApi.get(`/${projectId}/ocr-configs`);
      console.log('取得したOCR設定の生データ:', response.data);
      
      const configs = response.data || [];
      console.log('設定一覧:', configs);
      configs.forEach((config, index) => {
        console.log(`設定 ${index + 1}:`, {
          id: config.id,
          name: config.name,
          hasBlocks: !!config.blocks,
          blocks: config.blocks,
          hasHeaderItems: !!config.headerItems,
          headerItems: config.headerItems,
          hasTableItems: !!config.tableItems,
          tableItems: config.tableItems
        });
      });
      
      setSavedConfigs(configs);
    } catch (error) {
      console.error('設定の読み込みに失敗しました:', error);
      console.error('エラー詳細:', error.response?.data);
      setSavedConfigs([]);
    }
  };

  // プロジェクト情報の取得
  const fetchProject = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await projectApi.getProject(projectId);
      setProject(response.data);
      
      // 参照データ一覧も取得
      await fetchReferenceDataList();
    } catch (error) {
      console.error('Failed to fetch project:', error);
      setError('プロジェクト情報の取得に失敗しました。');
      
      if (error.response?.status === 404) {
        toast({
          title: 'プロジェクトが見つかりません',
          description: '指定されたプロジェクトが存在しないか、アクセス権限がありません。',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/dashboard');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ファイルがアップロードされたときのハンドラー
  const handleFileUpload = (file) => {
    console.log('File uploaded:', file);
    setUploadedFile(file);
    
    // ファイルタイプに応じてプレビューを設定
    if (file && file.type) {
      if (file.type.startsWith('image/')) {
        // 画像ファイルの場合
        const reader = new FileReader();
        reader.onloadend = () => {
          console.log('Image preview generated');
          setPreview(reader.result);
          setIsUploadSectionCollapsed(true);
        };
        reader.readAsDataURL(file);
      } else if (file.type === 'application/pdf') {
        // PDFファイルの場合
        console.log('PDF file uploaded, preparing PDF preview');
        const pdfUrl = URL.createObjectURL(file);
        setPreview(pdfUrl); // PDFファイルのObjectURLを設定
        setIsUploadSectionCollapsed(true);
      } else {
        // その他のファイル
        console.log('Other file type, no preview available');
        setPreview(null);
        setIsUploadSectionCollapsed(true);
      }
    } else {
      setPreview(null);
      setIsUploadSectionCollapsed(true);
    }
  };

  // 再アップロードのハンドラー
  const handleReupload = () => {
    // ObjectURLをクリーンアップ
    if (preview && typeof preview === 'string' && preview.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }
    setUploadedFile(null);
    setPreview(null);
    setIsUploadSectionCollapsed(false);
  };

  // 設定変更のハンドラー
  const handleConfigChange = (newConfig) => {
    setOcrConfig(newConfig);
  };

  // 設定読み込み時のハンドラー（元の設定として記録）
  const handleConfigLoad = (loadedConfig) => {
    setOriginalConfig(JSON.parse(JSON.stringify(loadedConfig))); // ディープコピー
  };

  // 設定が変更されているかチェック
  const hasConfigChanged = () => {
    if (!originalConfig) return true; // 元の設定がない場合は変更ありとみなす
    
    // 設定の深い比較
    const isEqual = (obj1, obj2) => {
      return JSON.stringify(obj1) === JSON.stringify(obj2);
    };

    return !isEqual(originalConfig, ocrConfig);
  };

  // ブロック操作関数
  const addBlock = () => {
    const newBlock = {
      id: v4(),
      blockName: `ブロック${ocrConfig.blocks.length + 1}`,
      blockType: "header",
      items: [
        { id: v4(), itemName: "", columnName: "" }
      ]
    };
    
    setOcrConfig(prev => ({
      ...prev,
      blocks: [...prev.blocks, newBlock]
    }));
  };

  const deleteBlock = (blockId) => {
    setOcrConfig(prev => ({
      ...prev,
      blocks: prev.blocks.filter(block => block.id !== blockId)
    }));
  };

  const updateBlockName = (blockId, newName) => {
    setOcrConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => 
        block.id === blockId ? { ...block, blockName: newName } : block
      )
    }));
  };

  const updateBlockType = (blockId, newType) => {
    setOcrConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => 
        block.id === blockId ? { ...block, blockType: newType } : block
      )
    }));
  };

  const addItemToBlock = (blockId) => {
    const newItem = { id: v4(), itemName: "", columnName: "" };
    
    setOcrConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => 
        block.id === blockId 
          ? { ...block, items: [...block.items, newItem] }
          : block
      )
    }));
  };

  const deleteItemFromBlock = (blockId, itemId) => {
    setOcrConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => 
        block.id === blockId 
          ? { ...block, items: block.items.filter(item => item.id !== itemId) }
          : block
      )
    }));
  };

  const updateItemInBlock = (blockId, itemId, field, value) => {
    setOcrConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => 
        block.id === blockId 
          ? {
              ...block, 
              items: block.items.map(item => 
                item.id === itemId ? { ...item, [field]: value } : item
              )
            }
          : block
      )
    }));
  };

  // 保存された設定を読み込み
  const handleLoadSavedConfig = (configId) => {
    console.log('handleLoadSavedConfig called with:', configId, typeof configId);
    
    if (!configId) {
      setSelectedSavedConfig('');
      return;
    }

    // UUIDと整数の両方の形式に対応した柔軟な比較
    const config = savedConfigs.find(c => {
      // 文字列として比較（UUIDの場合）
      if (String(c.id) === String(configId)) {
        return true;
      }
      // 整数として比較（数値IDの場合）
      if (typeof c.id === 'number' && !isNaN(parseInt(configId))) {
        return c.id === parseInt(configId);
      }
      return false;
    });
    
    // 選択されたconfigIdを先に設定
    setSelectedSavedConfig(configId);
    
    if (config) {
      console.log('設定を読み込み中:', config);
      
      // ブロック形式の設定を読み込み
      if (config.blocks && config.blocks.length > 0) {
        console.log('新しいブロック形式の設定を読み込み');
        // 新しいブロック形式
        const newBlocks = config.blocks.map(block => ({
          ...block,
          id: v4(), // 新しいIDを生成
          items: block.items ? block.items.map(item => ({
            ...item,
            id: v4() // アイテムにも新しいIDを生成
          })) : []
        }));
        
        console.log('生成されたブロック:', newBlocks);
        
        setOcrConfig(prev => ({
          ...prev,
          blocks: newBlocks,
          customPrompt: config.customPrompt || ''
        }));
      } else if (config.headerItems || config.tableItems) {
        console.log('古い形式の設定を新しい形式に変換');
        // 古い形式の設定を新しい形式に変換
        const blocks = [];
        
        if (config.headerItems && config.headerItems.length > 0) {
          blocks.push({
            id: v4(),
            blockName: "基本情報",
            blockType: "header",
            items: config.headerItems.map(item => ({
              ...item,
              id: v4()
            }))
          });
        }
        
        if (config.tableItems && config.tableItems.length > 0) {
          blocks.push({
            id: v4(),
            blockName: "明細データ",
            blockType: "table",
            items: config.tableItems.map(item => ({
              ...item,
              id: v4()
            }))
          });
        }
        
        console.log('変換されたブロック:', blocks);
        
        setOcrConfig(prev => ({
          ...prev,
          blocks: blocks,
          customPrompt: config.customPrompt || ''
        }));
      } else {
        console.warn('設定にブロックデータが見つかりません:', config);
      }
      
      setOriginalConfig(JSON.parse(JSON.stringify(config))); // 元の設定として記録
      
      toast({
        title: '設定を読み込みました',
        description: `「${config.name}」の設定を適用しました`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } else {
      console.error('設定が見つかりません:', configId, savedConfigs);
      console.error('利用可能な設定:', savedConfigs.map(c => ({ id: c.id, name: c.name })));
    }
  };

  // プリセット変更ハンドラー（ブロックシステム対応）
  const handlePresetChange = (preset) => {
    setOcrConfig(prev => ({
      ...prev,
      preset
    }));

    // プリセットに応じた初期ブロックを設定
    if (preset === "invoice") {
      setOcrConfig(prev => ({
        ...prev,
        preset,
        blocks: [
          {
            id: v4(),
            blockName: "請求書情報",
            blockType: "header",
            items: [
              { id: v4(), itemName: "請求書番号", columnName: "invoice_no" },
              { id: v4(), itemName: "発行日", columnName: "issue_date" },
              { id: v4(), itemName: "会社名", columnName: "company_name" },
            ]
          },
          {
            id: v4(),
            blockName: "明細項目",
            blockType: "table",
            items: [
              { id: v4(), itemName: "品目", columnName: "item" },
              { id: v4(), itemName: "数量", columnName: "quantity" },
              { id: v4(), itemName: "単価", columnName: "unit_price" },
              { id: v4(), itemName: "金額", columnName: "amount" },
            ]
          }
        ]
      }));
    } else if (preset === "receipt") {
      setOcrConfig(prev => ({
        ...prev,
        preset,
        blocks: [
          {
            id: v4(),
            blockName: "レシート情報",
            blockType: "header",
            items: [
              { id: v4(), itemName: "店舗名", columnName: "store_name" },
              { id: v4(), itemName: "日付", columnName: "date" },
              { id: v4(), itemName: "時間", columnName: "time" },
            ]
          },
          {
            id: v4(),
            blockName: "購入商品",
            blockType: "table",
            items: [
              { id: v4(), itemName: "品目", columnName: "item" },
              { id: v4(), itemName: "金額", columnName: "price" },
            ]
          }
        ]
      }));
    } else if (preset === "business_card") {
      setOcrConfig(prev => ({
        ...prev,
        preset,
        blocks: [
          {
            id: v4(),
            blockName: "名刺情報",
            blockType: "header",
            items: [
              { id: v4(), itemName: "氏名", columnName: "name" },
              { id: v4(), itemName: "会社名", columnName: "company" },
              { id: v4(), itemName: "役職", columnName: "title" },
              { id: v4(), itemName: "電話番号", columnName: "phone" },
              { id: v4(), itemName: "メールアドレス", columnName: "email" },
              { id: v4(), itemName: "住所", columnName: "address" },
            ]
          }
        ]
      }));
    }
  };

  // プロンプトへの追加指示変更ハンドラー
  const handleCustomPromptChange = (customPrompt) => {
    setOcrConfig(prev => ({
      ...prev,
      customPrompt
    }));
  };

  // OCR処理の実行
  const handleProcessOCR = async () => {
    if (!uploadedFile) {
      toast({
        title: '画像ファイルが選択されていません',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // 基本プロンプトモード時のみ設定内容の検証を実行
    if (promptMode === 'basic') {
      const allItemsFilled = ocrConfig.blocks.every(block => 
        block.items.every(item => item.itemName.trim() !== "")
      );

      if (!allItemsFilled) {
        toast({
          title: '読み取り項目が未入力です',
          description: 'すべてのブロック内の「読み取りたい項目名」を入力してください。',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    }

    // オリジナルプロンプトモード時の検証
    if (promptMode === 'original' && !selectedOriginalPrompt) {
      toast({
        title: 'オリジナルプロンプトが選択されていません',
        description: 'オリジナルプロンプトを選択してください。',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // 基本プロンプトモードで設定が変更されている場合のみポップアップを表示
    if (promptMode === 'basic' && hasConfigChanged()) {
      onOpen();
    } else {
      // 変更がない場合、またはオリジナルプロンプトモードの場合は直接OCR処理を実行
      await executeOCR();
    }
  };

  // 設定保存の確認処理
  const handleSaveConfiguration = async (shouldSave) => {
    onClose();
    
    if (shouldSave && configName.trim()) {
      try {
        // ブロック形式の設定を保存
        const configData = {
          name: configName.trim(),
          blocks: ocrConfig.blocks,
          customPrompt: ocrConfig.customPrompt || ''
        };

        console.log(`OCR設定をプロジェクトに保存中: /projects/${projectId}/ocr-configs`, configData);
        await projectApi.post(`/${projectId}/ocr-configs`, configData);
        
        // 設定一覧を再取得
        await fetchSavedConfigs();
        
        toast({
          title: '設定が保存されました',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error('設定保存に失敗:', error);
        toast({
          title: '設定の保存に失敗しました',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
    
    // OCR処理を実行
    await executeOCR();
  };

  // 実際のOCR処理実行
  const executeOCR = async () => {
    let finalPrompt = '';
    
    // プロンプトモードに応じて適切なプロンプトを取得
    if (promptMode === 'basic') {
      // 基本プロンプトモード：従来通りの自動生成
      finalPrompt = generatePrompt(ocrConfig);
    } else if (promptMode === 'original') {
      // オリジナルプロンプトモード：選択されたプロンプトの内容を使用
      const selectedPrompt = originalPromptsList.find(p => p.id === selectedOriginalPrompt);
      if (selectedPrompt) {
        finalPrompt = selectedPrompt.content;
      } else {
        toast({
          title: 'プロンプトの取得に失敗しました',
          description: '選択されたオリジナルプロンプトが見つかりません。',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    }
    
    setIsProcessing(true);
    
    // FormDataの作成
    const formData = new FormData();
    formData.append('file', uploadedFile);
    formData.append('ai_provider', aiProvider); // 選択されたAIプロバイダーを使用
    
    // 最終的なプロンプトを追加
    formData.append('custom_prompt', finalPrompt);
    
    // 参照データが選択されている場合は追加
    if (useReferenceData && selectedReferenceData.length > 0) {
      // 複数の参照データIDを配列として送信
      selectedReferenceData.forEach(id => {
        formData.append('reference_data_ids', id);
      });
    }
    
    try {
      const response = await projectApi.processFile(projectId, formData);
      
      toast({
        title: 'OCR処理が完了しました',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      // 処理結果ページに遷移
      navigate(`/project/${projectId}/result/${response.data.result_id}`);
      
    } catch (error) {
      console.error('現在、生成AIからの返答が遅れています。プロジェクトトップに戻り、時間をおいてリロードしてください:', error);
      toast({
        title: "GPT-5は処理に最大10分程度かかる場合があります。しばらくお待ちください。タイムアウトした場合は、プロジェクトトップに戻り、時間をおいてリロードしてください",
        description: error.response?.data?.detail || '',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // プロンプトの自動生成（ブロックシステム対応）
  const generatePrompt = (config) => {
    const { blocks, customPrompt } = config;
    
    if (!blocks || blocks.length === 0) {
      return customPrompt || '';
    }
    
    // ヘッダータイプのブロック（基本情報）
    const headerBlocks = blocks.filter(block => block.blockType === 'header');
    // テーブルタイプのブロック（明細データ）
    const tableBlocks = blocks.filter(block => block.blockType === 'table');
    
    let prompt = '添付の画像からデータを下記の点に注意して抽出してください。\n\n';
    
    // ブロック構造の説明
    if (headerBlocks.length > 0 || tableBlocks.length > 0) {
      prompt += '## データ構造\n';
      
      // 基本情報ブロックの説明
      headerBlocks.forEach(block => {
        const itemNames = block.items.map(item => item.itemName).filter(name => name.trim());
        if (itemNames.length > 0) {
          prompt += `- ${block.blockName}: {${itemNames.join('、')}} を単一のオブジェクトとして抽出\n`;
        }
      });
      
      // 明細データブロックの説明
      tableBlocks.forEach(block => {
        const itemNames = block.items.map(item => item.itemName).filter(name => name.trim());
        if (itemNames.length > 0) {
          prompt += `- ${block.blockName}: {${itemNames.join('、')}} を配列形式で抽出\n`;
        }
      });
      
      prompt += '\n';
    }
    
    // 項目マッピングの説明
    const allItems = blocks.flatMap(block => 
      block.items.map(item => ({
        ...item,
        blockName: block.blockName,
        blockType: block.blockType
      }))
    ).filter(item => item.itemName.trim());
    
    if (allItems.length > 0) {
      prompt += '## 項目マッピング\n';
      allItems.forEach(item => {
        const outputName = item.columnName ? item.columnName : item.itemName;
        prompt += `- "${outputName}"は"${item.itemName}"から抽出してください\n`;
      });
      prompt += '\n';
    }
    
    // プロンプトへの追加指示
    if (customPrompt) {
      prompt += `## 追加指示\n${customPrompt}\n\n`;
    }
    
    // 出力フォーマット
    prompt += '## 出力フォーマット\n';
    prompt += '次の単一のJSON形式で出力してください：\n\n```json\n{\n';
    
    // 全ブロックを単一のJSONオブジェクトとして構造化
    const allValidBlocks = blocks.filter(block => 
      block.items.some(item => item.itemName.trim())
    );
    
    allValidBlocks.forEach((block, blockIndex) => {
      const validItems = block.items.filter(item => item.itemName.trim());
      if (validItems.length > 0) {
        const isLast = blockIndex === allValidBlocks.length - 1;
        
        if (block.blockType === 'header') {
          // ヘッダーブロック：オブジェクト形式
          prompt += `  "${block.blockName}": {\n`;
          validItems.forEach((item, itemIndex) => {
            const outputName = item.columnName || item.itemName;
            const isLastItem = itemIndex === validItems.length - 1;
            prompt += `    "${outputName}": "..."${isLastItem ? '' : ','}\n`;
          });
          prompt += `  }${isLast ? '' : ','}\n`;
        } else {
          // テーブルブロック：配列形式
          prompt += `  "${block.blockName}": [\n    {\n`;
          validItems.forEach((item, itemIndex) => {
            const outputName = item.columnName || item.itemName;
            const isLastItem = itemIndex === validItems.length - 1;
            prompt += `      "${outputName}": "..."${isLastItem ? '' : ','}\n`;
          });
          prompt += '    },\n    {\n';
          validItems.forEach((item, itemIndex) => {
            const outputName = item.columnName || item.itemName;
            const isLastItem = itemIndex === validItems.length - 1;
            prompt += `      "${outputName}": "..."${isLastItem ? '' : ','}\n`;
          });
          prompt += `    }\n  ]${isLast ? '' : ','}\n`;
        }
      }
    });
    
    prompt += '}\n```\n\n';
    
    return prompt.trim();
  };

  // コンポーネント初期化時にプロジェクト情報を取得
  useEffect(() => {
    fetchProject();
    fetchReferenceDataList();
    fetchOriginalPromptsList();
    fetchSavedConfigs();
  }, [projectId]);

  // ローディング中
  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="50vh">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="brand.500"
          size="xl"
        />
      </Flex>
    );
  }

  // エラー時
  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* ヘッダー */}
      <Flex
        justify="space-between"
        align="center"
        mb={6}
        bg={useColorModeValue('white', 'gray.800')}
        p={4}
        borderRadius="lg"
        boxShadow="sm"
      >
        <HStack>
          <Button
            leftIcon={<FiArrowLeft />}
            variant="ghost"
            onClick={() => navigate(`/project/${projectId}`)}
          >
            プロジェクトに戻る
          </Button>
          <Heading size="md">{project?.name} - OCR処理</Heading>
        </HStack>
      </Flex>

      {/* メインコンテンツ */}
      <Grid templateColumns={{ base: "1fr", lg: "2fr 3fr" }} gap={6}>
        {/* 左側：画像アップロード */}
        <GridItem>
          <VStack spacing={6} align="stretch">
            {/* アップロード部分（折りたたみ可能） */}
            <Box
              bg={useColorModeValue('white', 'gray.800')}
              borderRadius="lg"
              boxShadow="sm"
            >
              <Flex
                p={4}
                justify="space-between"
                align="center"
                onClick={() => setIsUploadSectionCollapsed(!isUploadSectionCollapsed)}
                cursor="pointer"
                _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}
                borderRadius="lg"
              >
                <Heading size="sm">
                  {uploadedFile ? `元画像: ${uploadedFile.name}` : '元画像'}
                </Heading>
                <HStack>
                  {uploadedFile && (
                    <IconButton
                      icon={<FiRefreshCw />}
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReupload();
                      }}
                      aria-label="再アップロード"
                      title="再アップロード"
                    />
                  )}
                  <IconButton
                    icon={isUploadSectionCollapsed ? <FiChevronDown /> : <FiChevronUp />}
                    size="sm"
                    variant="ghost"
                    aria-label="toggle upload section"
                  />
                </HStack>
              </Flex>
              
              <Collapse in={!isUploadSectionCollapsed}>
                <Box p={4} pt={0}>
                  <FileUploader
                    onFileUpload={handleFileUpload}
                    isUploading={false}
                    accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
                  />
                </Box>
              </Collapse>
            </Box>
            
            {/* プレビュー部分 */}
            {preview && (
              <Box
                bg={useColorModeValue('white', 'gray.800')}
                p={4}
                borderRadius="lg"
                boxShadow="sm"
              >
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading size="sm">プレビュー</Heading>
                  <Button
                    size="sm"
                    leftIcon={<FiRefreshCw />}
                    variant="outline"
                    onClick={handleReupload}
                  >
                    ファイルを変更
                  </Button>
                </Flex>
                
                {/* ファイルタイプに応じてプレビューを切り替え */}
                {uploadedFile && uploadedFile.type === 'application/pdf' ? (
                  <object
                    data={preview}
                    type="application/pdf"
                    width="100%"
                    height="950px"
                    style={{ border: 'none' }}
                  >
                    <embed
                      src={preview}
                      type="application/pdf"
                      width="100%"
                      height="950px"
                      style={{ border: 'none' }}
                    />
                    <p>PDFを表示できません。</p>
                  </object>
                ) : (
                  <ImagePreviewWithZoom
                    src={preview}
                    alt="アップロード画像プレビュー"
                  />
                )}
              </Box>
            )}
          </VStack>
        </GridItem>
        
        {/* 右側：OCR設定 */}
        <GridItem>
          <VStack spacing={6} align="stretch">
            {/* プロンプト選択 */}
            <Box
              bg={useColorModeValue('white', 'gray.800')}
              p={4}
              borderRadius="md"
              boxShadow="sm"
            >
              <VStack spacing={4} align="stretch">
                <Heading size="sm">プロンプト選択</Heading>
                
                <FormControl>
                  <FormLabel>使用するプロンプト</FormLabel>
                  <Select
                    value={promptMode}
                    onChange={(e) => {
                      setPromptMode(e.target.value);
                      if (e.target.value === 'basic') {
                        setSelectedOriginalPrompt('');
                      }
                    }}
                  >
                    <option value="basic">基本読み取り設定を使用</option>
                    <option value="original">オリジナルプロンプトを使用</option>
                  </Select>
                </FormControl>

                {promptMode === 'original' && (
                  <FormControl>
                    <FormLabel>オリジナルプロンプト</FormLabel>
                    {originalPromptsList.length === 0 ? (
                      <Text fontSize="sm" color="gray.500">
                        オリジナルプロンプトが登録されていません。プロジェクトの「オリジナルプロンプト」タブから作成してください。
                      </Text>
                    ) : (
                      <Select
                        placeholder="プロンプトを選択してください"
                        value={selectedOriginalPrompt}
                        onChange={(e) => setSelectedOriginalPrompt(e.target.value)}
                      >
                        {originalPromptsList.map((prompt) => (
                          <option key={prompt.id} value={prompt.id}>
                            {prompt.name}
                          </option>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                )}
              </VStack>
            </Box>
            
            {/* 基本読み取り設定（基本プロンプトモード時のみ表示） */}
            {promptMode === 'basic' && (
              <Box
                bg={useColorModeValue('white', 'gray.800')}
                p={4}
                borderRadius="md"
                boxShadow="sm"
              >
                <VStack spacing={4} align="stretch">
                  <Flex justify="space-between" align="center">
                    <Heading size="sm">ブロック設定</Heading>
                    <Button
                      size="sm"
                      colorScheme="brand"
                      leftIcon={<FiPlus />}
                      onClick={addBlock}
                    >
                      ブロックを追加
                    </Button>
                  </Flex>
                  
                  {/* 保存された設定の読み込み */}
                  {savedConfigs.length > 0 && (
                    <FormControl>
                      <FormLabel fontSize="sm">保存済み設定から読み込み</FormLabel>
                      <Flex gap={2}>
                        <Select
                          placeholder="設定を選択してください"
                          value={selectedSavedConfig}
                          onChange={(e) => handleLoadSavedConfig(e.target.value)}
                          size="sm"
                          flex="1"
                        >
                          {savedConfigs.map((config) => (
                            <option key={config.id} value={String(config.id)}>
                              {config.name}
                            </option>
                          ))}
                        </Select>
                        {selectedSavedConfig && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedSavedConfig('');
                              console.log('設定選択をクリア');
                            }}
                          >
                            クリア
                          </Button>
                        )}
                      </Flex>
                    </FormControl>
                  )}
                  
                  {ocrConfig.blocks.map((block, blockIndex) => (
                    <Box key={block.id} border="1px" borderColor="gray.200" borderRadius="md" p={4}>
                      <VStack spacing={3} align="stretch">
                        {/* ブロック名とタイプの設定 */}
                        <Flex gap={3} align="end">
                          <FormControl>
                            <FormLabel fontSize="sm">ブロック名</FormLabel>
                            <Input
                              value={block.blockName}
                              onChange={(e) => updateBlockName(block.id, e.target.value)}
                              placeholder="ブロック名を入力"
                            />
                          </FormControl>
                          <FormControl width="200px">
                            <FormLabel fontSize="sm">ブロックタイプ</FormLabel>
                            <Select
                              value={block.blockType}
                              onChange={(e) => updateBlockType(block.id, e.target.value)}
                            >
                              <option value="header">基本情報</option>
                              <option value="table">テーブル</option>
                            </Select>
                          </FormControl>
                          {ocrConfig.blocks.length > 1 && (
                            <IconButton
                              icon={<FiMinus />}
                              colorScheme="red"
                              variant="outline"
                              onClick={() => deleteBlock(block.id)}
                              aria-label="ブロックを削除"
                            />
                          )}
                        </Flex>
                        
                        {/* 項目設定 */}
                        <Box>
                          <Flex justify="space-between" align="center" mb={2}>
                            <Text fontSize="sm" fontWeight="medium">項目設定</Text>
                            <Button
                              size="xs"
                              leftIcon={<FiPlus />}
                              onClick={() => addItemToBlock(block.id)}
                            >
                              項目を追加
                            </Button>
                          </Flex>
                          
                          <VStack spacing={2}>
                            {block.items.map((item, itemIndex) => (
                              <Flex key={item.id} gap={2} align="center" w="full">
                                <FormControl>
                                  <Input
                                    value={item.itemName}
                                    onChange={(e) => updateItemInBlock(block.id, item.id, 'itemName', e.target.value)}
                                    placeholder="読み取りたい項目名"
                                    size="sm"
                                  />
                                </FormControl>
                                <FormControl>
                                  <Input
                                    value={item.columnName}
                                    onChange={(e) => updateItemInBlock(block.id, item.id, 'columnName', e.target.value)}
                                    placeholder="出力時の列名（任意）"
                                    size="sm"
                                  />
                                </FormControl>
                                {block.items.length > 1 && (
                                  <IconButton
                                    icon={<FiMinus />}
                                    size="sm"
                                    colorScheme="red"
                                    variant="ghost"
                                    onClick={() => deleteItemFromBlock(block.id, item.id)}
                                    aria-label="項目を削除"
                                  />
                                )}
                              </Flex>
                            ))}
                          </VStack>
                        </Box>
                        
                        {/* ブロックタイプの説明 */}
                        <Text fontSize="xs" color="gray.500">
                          {block.blockType === 'header' 
                            ? '💡 基本情報: 単一のオブジェクトとして抽出されます（例：請求書番号、発行日など）'
                            : '💡 テーブル: 配列形式で抽出されます（例：商品明細、購入履歴など）'
                          }
                        </Text>
                      </VStack>
                    </Box>
                  ))}
                </VStack>
              </Box>
            )}
            
            {/* プロンプト追記設定 */}
            <Box
              bg={useColorModeValue('white', 'gray.800')}
              p={4}
              borderRadius="md"
              boxShadow="sm"
            >
              <VStack spacing={4} align="stretch">
                <Heading size="sm">プロンプト追記設定</Heading>
                
                {/* AIプロバイダー選択 */}
                <FormControl>
                  <FormLabel>使用するAI</FormLabel>
                  <Select 
                    value={aiProvider} 
                    onChange={(e) => setAiProvider(e.target.value)}
                  >
                    <option value="openai">OpenAI (GPT-4o)</option>
                    <option value="openai-gpt5">OpenAI (GPT-5) 🚀</option>
                    <option value="claude">Anthropic Claude</option>
                    <option value="gemini">Google Gemini</option>
                    <option value="mistral">Mistral (Pixtral) 🆕</option>
                  </Select>
                </FormControl>
                
                {/* 参照データ選択 */}
                <Box>
                  <Checkbox
                    isChecked={useReferenceData}
                    onChange={(e) => {
                      setUseReferenceData(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedReferenceData([]);
                      }
                    }}
                  >
                    参照データを使用する
                  </Checkbox>
                  
                  {useReferenceData && (
                    <Box mt={3}>
                      <FormControl>
                        <FormLabel fontSize="sm">参照データを選択（複数選択可能）</FormLabel>
                        {referenceDataList.length === 0 ? (
                          <Alert status="info" size="sm">
                            <AlertIcon />
                            <Text fontSize="sm">
                              参照データが登録されていません。
                              <Text as="span" color="blue.500" cursor="pointer" textDecoration="underline" ml={1}>
                                プロジェクト設定で登録
                              </Text>
                              してください。
                            </Text>
                          </Alert>
                        ) : (
                          <CheckboxGroup
                            value={selectedReferenceData}
                            onChange={setSelectedReferenceData}
                          >
                            <Stack spacing={2} pl={4}>
                              {referenceDataList.map((data) => (
                                <Checkbox key={data.id} value={data.id} size="sm">
                                  <Text fontSize="sm">{data.name}</Text>
                                </Checkbox>
                              ))}
                            </Stack>
                          </CheckboxGroup>
                        )}
                      </FormControl>
                      
                      {selectedReferenceData.length > 0 && (
                        <Text fontSize="xs" color="gray.600" mt={2}>
                          💡 選択した{selectedReferenceData.length}件の参照データは、AIがより正確な推論を行うためのガイドとして使用されます。
                        </Text>
                      )}
                    </Box>
                  )}
                </Box>
                
                {/* プリセット設定 - 非表示 */}
                {/* 
                <FormControl>
                  <FormLabel>プリセット設定</FormLabel>
                  <Select 
                    value={ocrConfig.preset} 
                    onChange={(e) => handlePresetChange(e.target.value)}
                  >
                    <option value="custom">カスタム設定</option>
                    <option value="invoice">請求書</option>
                    <option value="receipt">領収書</option>
                    <option value="business_card">名刺</option>
                  </Select>
                </FormControl>
                */}

                {/* プロンプトへの追加指示 */}
                <FormControl>
                  <FormLabel>プロンプトへの追加指示</FormLabel>
                  <Text fontSize="xs" color="gray.600" mb={2}>
                    細かくAIへ指示を出したい場合に追記できます
                  </Text>
                  <Textarea
                    placeholder="例：特定の項目の抽出方法、フォーマット指定など"
                    value={ocrConfig.customPrompt}
                    onChange={(e) => handleCustomPromptChange(e.target.value)}
                    rows={4}
                  />
                </FormControl>
              </VStack>
            </Box>
            
            <Flex justify="flex-end">
              <Button
                colorScheme="brand"
                size="lg"
                leftIcon={<FiPlay />}
                onClick={handleProcessOCR}
                isLoading={isProcessing}
                loadingText="処理中..."
                isDisabled={!uploadedFile}
              >
                テキスト抽出を実行
              </Button>
            </Flex>
          </VStack>
        </GridItem>
      </Grid>

      {/* 設定保存確認モーダル */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>現在の設定を登録しますか？</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>現在の設定を保存して再利用できるようにしますか？</Text>
              <FormControl>
                <FormLabel>設定名</FormLabel>
                <Input
                  placeholder="設定名を入力してください"
                  value={configName}
                  onChange={(e) => setConfigName(e.target.value)}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => handleSaveConfiguration(false)}>
              保存せずに実行
            </Button>
            <Button 
              colorScheme="brand" 
              onClick={() => handleSaveConfiguration(true)}
              isDisabled={!configName.trim()}
            >
              保存して実行
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default OCRProcessing;